import React, { useState, useEffect } from 'react';
import { Eye, Plus, PencilSimple, Trash, UsersThree } from '@phosphor-icons/react';
import GenericTables from '../../../shared/components/ui/GenericTables';
import ActionMenu from '../../../shared/components/ui/ActionMenu';
import ModalCreationEditing from '../../../shared/components/ui/ModalCreationEditing';
import PageHeader from '../../../shared/components/ui/PageHeader';
import ConfirmDialog from '../../../shared/components/ui/ConfirmDialog';
import ExpansionPanel from '../../../shared/components/ui/ExpansionPanel';
import { userService } from '../../../services/users/userService.js';
import { teamAssignmentService } from '../services/teamAssignmentService.js';
import { teamService } from '../../../services/teams/teamService.js';
import { teamTaskService } from '../services/teamTaskService.js';
import { teamVehicleService } from '../services/teamVehicleService.js';
import { useGuidedTourStep } from '../../../shared/hooks/useGuidedTour.js';

const TeamManagement = () => {
  const [teams, setTeams] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalTeams, setTotalTeams] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [showTeamModal, setShowTeamModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [teamModalMode, setTeamModalMode] = useState('create');
  const [teamForm, setTeamForm] = useState({
    name: '',
    vehicleId: '',
    description: '',
    startDate: '',
    endDate: '',
    maxMembers: 2,
  });
  const [selectedTechnicians, setSelectedTechnicians] = useState([]);
  const [teamFormTouched, setTeamFormTouched] = useState({
    name: false,
    vehicleId: false,
    description: false,
    startDate: false,
    endDate: false,
    maxMembers: false,
  });
  const [teamLimits, setTeamLimits] = useState([]);
  const [teamLimitsLoading, setTeamLimitsLoading] = useState(false);
  const [teamLimitsRowStatus, setTeamLimitsRowStatus] = useState({});
  const [notification, setNotification] = useState({
    show: false,
    type: 'success',
    message: '',
    origin: ''
  });
  const [deleteConfirm, setDeleteConfirm] = useState({
    isOpen: false,
    team: null,
    activeTasks: 0
  });

  // Recorrido guiado (teamsTour): abre el modal de creación en sus pasos y lo cierra al salir.
  // La tabla (min-width 1200px) se desplaza para mostrar las acciones: al salir de ese paso vuelve al inicio.
  const [tourStep, setTourStep] = useState(null);
  useGuidedTourStep((step) => {
    if (step?.modal === 'team-create' && !tourStep?.modal) handleOpenCreateTeam();
    if (!step?.modal && tourStep?.modal) setShowTeamModal(false);
    if (tourStep?.element?.includes('team-actions')) {
      document.querySelectorAll('[data-tour="teams-table"] .overflow-x-auto').forEach((el) => { el.scrollLeft = 0; });
    }
    setTourStep(step);
  });

  const showNotification = (message, type = 'success', origin = '') => {
    setNotification({ show: true, message, type, origin });
    setTimeout(() => {
      const notificationEl = document.querySelector('.notification-message');
      if (notificationEl) {
        notificationEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success', origin: '' });
    }, 3000);
  };

  const clearNotification = () => setNotification({ show: false, message: '', type: 'success', origin: '' });

  const getRequestErrorMessage = (error, fallback = 'Ocurrió un error inesperado') => {
    return error?.message || error?.payload?.message || fallback;
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize]);

  const formatDateToLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const addYears = (date, years) => {
    const next = new Date(date);
    next.setFullYear(next.getFullYear() + years);
    return next;
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [teamsRes, assignmentsRes, techniciansRes, vehiclesRes] = await Promise.all([
        teamService.getTeams({ pageSize, pageCurrent: currentPage - 1, hasGeoData: false }).catch(() => []),
        teamAssignmentService.getAssignments().catch(() => []),
        userService.getTechnicians().catch(() => []),
        teamVehicleService.getVehicles().catch(() => [])
      ]);

      const teamsResult = Array.isArray(teamsRes) ? teamsRes : teamsRes?.teams || [];

      setTeams(teamsResult || []);
      setAssignments(assignmentsRes || []);
      setTechnicians(techniciansRes || []);
      setVehicles(Array.isArray(vehiclesRes) ? vehiclesRes : []);

      if (!Array.isArray(teamsRes)) {
        const totalItems = Number(teamsRes?.pagination?.totalItems) || teamsResult.length;
        const resolvedPageSize = Number(teamsRes?.pagination?.pageSize) || pageSize;
        const calculatedPages = totalItems > 0 ? Math.ceil(totalItems / resolvedPageSize) : 1;

        setTotalTeams(totalItems);
        setTotalPages(calculatedPages);
      } else {
        setTotalTeams(teamsResult.length);
        setTotalPages(1);
      }
    } catch (err) {
      console.error('Error al cargar datos:', err);
      setError('Error al cargar datos');
      setTeams([]);
      setTotalTeams(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamLimits = async (teamId) => {
    try {
      setTeamLimitsLoading(true);
      const limits = await teamService.getTeamLimits(teamId);
      setTeamLimits(Array.isArray(limits) ? limits : []);
    } catch (err) {
      console.error('Error al cargar límites del equipo:', err);
      setTeamLimits([]);
    } finally {
      setTeamLimitsLoading(false);
    }
  };

  const handleTeamLimitChange = (categoryName, value) => {
    const cleaned = value.replace(/[^0-9]/g, '');
    const taskLimit = cleaned === '' ? null : Number(cleaned);
    setTeamLimits(prev => prev.map(limit =>
      limit.categoryName === categoryName ? { ...limit, taskLimit } : limit
    ));
  };

  const handleTeamLimitBlur = async (categoryName) => {
    if (!editingTeam) return;
    const limit = teamLimits.find(l => l.categoryName === categoryName);
    if (!limit) return;

    setTeamLimitsRowStatus(prev => ({ ...prev, [categoryName]: 'saving' }));
    try {
      await teamService.updateTeamLimit(editingTeam.id, categoryName, limit.taskLimit);
      setTeamLimitsRowStatus(prev => ({ ...prev, [categoryName]: 'saved' }));
    } catch (err) {
      console.error('Error al guardar límite de categoría:', err);
      setTeamLimitsRowStatus(prev => ({ ...prev, [categoryName]: 'error' }));
    }
  };

  const isActiveAssignment = (assignment) => {
    // Considerar activo si no tiene fecha de fin; contemplar posibles claves alternativas
    const { leaveDate, leave_date, endDate, end_date, isActive, active } = assignment || {};
    if (typeof isActive === 'boolean') return isActive;
    if (typeof active === 'boolean') return active;
    return !(leaveDate || leave_date || endDate || end_date);
  };

  const getAssignmentTechnicians = (assignment) => {
    if (!assignment) return [];
    // Normalizar posibles formas de guardar técnicos en el backend
    const {
      technician1Id,
      technician2Id,
      technician3Id,
      technicianId,
      userEmail,
      userId,
      technicians,
      techniciansIds,
    } = assignment;

    const collected = [technician1Id, technician2Id, technician3Id, technicianId, userEmail, userId];
    if (Array.isArray(technicians)) collected.push(...technicians);
    if (Array.isArray(techniciansIds)) collected.push(...techniciansIds);
    return collected.filter(Boolean);
  };

  const isTechnicianAssigned = (email) => {
    return assignments.some(a => isActiveAssignment(a) && getAssignmentTechnicians(a).includes(email));
  };

  const isTechnicianAssignedElsewhere = (email, teamId) => {
    return assignments.some(a => isActiveAssignment(a) && a.teamId !== teamId && getAssignmentTechnicians(a).includes(email));
  };

  const isVehicleAssigned = (vehicleId, teamId) => {
    // Si el vehículo ya está en una asignación activa y no es del mismo equipo (al editar), se considera ocupado
    return assignments.some(a => isActiveAssignment(a) && a.vehicleId === vehicleId && (!teamId || a.teamId !== teamId));
  };

  const handleOpenCreateTeam = () => {
    const today = formatDateToLocal(new Date());
    const farFuture = formatDateToLocal(addYears(new Date(), 100));
    setEditingTeam(null);
    setTeamModalMode('create');
    setTeamForm({
      name: '',
      vehicleId: '',
      description: '',
      startDate: today,
      endDate: farFuture,
      maxMembers: 1,
    });
    setSelectedTechnicians([]);
    setTeamFormTouched({
      name: false,
      vehicleId: false,
      description: false,
      startDate: false,
      endDate: false,
      maxMembers: false,
    });
    setTeamLimits([]);
    setTeamLimitsRowStatus({});
    clearNotification();
    setShowTeamModal(true);
  };

  const handleOpenEditTeam = (team, mode = 'edit') => {
    const today = formatDateToLocal(new Date());
    setEditingTeam(team);
    setTeamModalMode(mode);

    // Buscar la asignación activa del equipo para obtener el vehicleId
    const activeAssignment = assignments.find(a => a.teamId === team.id && isActiveAssignment(a));
    const vehicleId = activeAssignment?.vehicleId || '';
    const date = activeAssignment?.date;

    setTeamForm({
      name: team.name || '',
      vehicleId: vehicleId,
      description: team.description || '',
      date: date,
      startDate: team.startDate || today,
      endDate: team.endDate || today,
      maxMembers: Math.min(Number(team.maxMembers || 0), 3),
    });
    setTeamFormTouched({
      name: false,
      vehicleId: false,
      description: false,
      startDate: false,
      endDate: false,
      maxMembers: false,
    });
    setTeamLimits([]);
    setTeamLimitsRowStatus({});
    fetchTeamLimits(team.id);

    // Preseleccionar técnicos del equipo activo (si hay)
    const activeTeamAssignments = assignments.filter(a => a.teamId === team.id && isActiveAssignment(a));
    const preselected = [];
    activeTeamAssignments.forEach(a => {
      getAssignmentTechnicians(a).forEach(id => preselected.push(id));
    });
    setSelectedTechnicians(preselected.slice(0, 3));
    clearNotification();
    setShowTeamModal(true);
  };

  const handleTeamFormChange = (e) => {
    const { name, value } = e.target;
    if (name === 'maxMembers') {
      const raw = value;
      const cleaned = raw.replace(/[^0-9]/g, '');
      const numeric = Number(cleaned);
      const num = Number.isNaN(numeric) || cleaned === ''
        ? 1
        : Math.max(1, Math.min(10, numeric));
      setTeamForm(prev => ({ ...prev, [name]: num }));
      // Ajustar el arreglo de técnicos seleccionados al rango 2-3
      setSelectedTechnicians(prev => {
        const next = [...prev];
        const targetLen = num;
        if (targetLen < next.length) return next.slice(0, targetLen);
        while (next.length < targetLen) next.push('');
        return next;
      });
    } else if (name === 'description') {
      // Limitar descripción a 255 caracteres
      const truncated = value.slice(0, 255);
      setTeamForm(prev => ({ ...prev, [name]: truncated }));
    } else {
      setTeamForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleTeamFieldBlur = (fieldName) => {
    setTeamFormTouched(prev => ({ ...prev, [fieldName]: true }));
  };

  const handleSelectTechnicianChange = (index, email) => {
    setSelectedTechnicians(prev => {
      const next = [...prev];
      // Evitar duplicados
      if (email && next.some((e, i) => i !== index && e === email)) {
        return next; // ignorar si ya está seleccionado en otro índice
      }
      next[index] = email;
      return next;
    });
  };

  const validateTeamForm = () => {
    if (!teamForm.name) return { isValid: false, missingField: 'Nombre del equipo' };
    if (!teamForm.description) return { isValid: false, missingField: "Descripción" };
    if (selectedTechnicians.length === 0 || !selectedTechnicians[0]) return { isValid: false, missingField: "Técnicos asignados" };

    return { isValid: true, missingField: null };
  };

  const handleDeleteTeam = async (team) => {
    // Obtener tareas activas del equipo (no finalizadas ni canceladas)
    let activeTasks = 0;
    try {
      const activeTasksCount = await teamTaskService.getActiveTodayTaskCountByTeam(team.id);
      activeTasks = activeTasksCount;
    } catch {
      // Si falla el fetch, ignorar — el warning simplemente no aparece
    }
    setDeleteConfirm({
      isOpen: true,
      team: team,
      message: `Hay ${activeTasks} tareas activas asignadas a este equipo. ¿Estás seguro de que quieres eliminarlo?`,
      activeTasks
    });
  };

  const confirmDeleteTeam = async () => {
    const team = deleteConfirm.team;
    setDeleteConfirm({ isOpen: false, team: null });

    if (!team) return;

    try {
      await teamService.deleteTeam(team.id);
      const deleteRes = { ok: true, status: 200, text: async () => '' };

      const text = await deleteRes.text();
      let data;
      try {
        data = text ? JSON.parse(text) : {};
      } catch (err) {
        data = { message: text };
      }

      if (!deleteRes.ok) {
        showNotification(data.message || `Error al eliminar el equipo: ${deleteRes.status}`, 'error');
        return;
      }

      showNotification('Equipo eliminado exitosamente', 'success');
      await fetchData();
    } catch (err) {
      console.error('Error eliminando equipo:', err);
      showNotification(getRequestErrorMessage(err, 'Error al eliminar el equipo'), 'error');
    }
  };

  const cancelDeleteTeam = () => {
    setDeleteConfirm({ isOpen: false, team: null, activeTasks: 0 });
  };

  const handleSaveTeam = async () => {
    try {
      // Validación específica de campos obligatorios
      const validation = validateTeamForm();
      if (!validation.isValid) {
        showNotification(`El campo "${validation.missingField}" es obligatorio`, 'error');
        return;
      }

      const today = formatDateToLocal(new Date());
      const farFuture = formatDateToLocal(addYears(new Date(), 100));

      if (!editingTeam) {
        // Crear equipo
        const teamPayload = {
          name: teamForm.name,
          description: teamForm.description,
          maxMembers: Number(teamForm.maxMembers) || 0,
        };
        const createdTeam = await teamService.createTeam(teamPayload);
        const createRes = { ok: true, status: 200, text: async () => JSON.stringify(createdTeam || {}) };
        const text = await createRes.text();
        let data;
        try {
          data = text ? JSON.parse(text) : {};
        }
        catch (err) {
          data = { message: text };
        }
        if (!createRes.ok) {
          showNotification(data.message || `HTTP error! status: ${createRes.status}`, 'error');
          return;
        }
        const newTeam = data; // ya está parseado del body; evitar leer dos veces
        // Eliminar técnicos vacíos
        const filteredTechnicians = selectedTechnicians.filter(t => t);

        // Crear asignación combinada con los técnicos seleccionados (inicio hoy)
        if (filteredTechnicians.length > 0) {
          const payload = {
            teamId: newTeam.id,
            technicianIds: filteredTechnicians,
            vehicleId: teamForm.vehicleId,
            startDate: today,
            endDate: farFuture,
          };
          const createdAssignment = await teamAssignmentService.createAssignment(payload);
          const assignRes = { ok: true, status: 200, text: async () => JSON.stringify(createdAssignment || {}) };
          const text = await assignRes.text();
          let data;
          try {
            data = text ? JSON.parse(text) : {};
          } catch (err) {
            data = { message: text };
          }
          if (!assignRes.ok) {
            showNotification(data.message || `HTTP error! status: ${assignRes.status}`, 'error');
            return
          }
        }
      } else {

        // Actualizar equipo existente con PUT
        const teamPayload = {
          name: teamForm.name,
          description: teamForm.description,
          maxMembers: Number(teamForm.maxMembers) || 0,
        };
        const updatedTeam = await teamService.updateTeam(editingTeam.id, teamPayload);
        const updateRes = { ok: true, status: 200, text: async () => JSON.stringify(updatedTeam || {}) };
        const text = await updateRes.text();
        let data;
        try {
          data = text ? JSON.parse(text) : {};
        }
        catch (err) {
          data = { message: text };
        }
        if (!updateRes.ok) {
          showNotification(data.message || `HTTP error! status: ${updateRes.status}`, 'error');
          return;
        }


        const newTeamId = editingTeam.id;

        // Verificar si hay cambios en las asignaciones (técnicos o vehículo)
        const activeAssignments = assignments.filter(a => a.teamId === editingTeam.id && isActiveAssignment(a));
        
        // Obtener técnicos actuales del equipo
        const currentTechnicians = [];
        activeAssignments.forEach(a => {
          getAssignmentTechnicians(a).forEach(id => {
            if (!currentTechnicians.includes(id)) {
              currentTechnicians.push(id);
            }
          });
        });
        
        // Obtener vehículo actual
        const currentVehicleId = activeAssignments.length > 0 ? activeAssignments[0].vehicleId : null;
        
        // Filtrar técnicos seleccionados
        const filteredTechnicians = selectedTechnicians.filter(t => t);
        
        // Verificar si hay cambios
        const techniciansChanged = 
          filteredTechnicians.length !== currentTechnicians.length ||
          !filteredTechnicians.every(tech => currentTechnicians.includes(tech)) ||
          !currentTechnicians.every(tech => filteredTechnicians.includes(tech));
        
        const vehicleChanged = currentVehicleId !== teamForm.vehicleId;
        
        // Solo actualizar asignaciones si hay cambios
        if (techniciansChanged || vehicleChanged) {
          const now = new Date().toISOString().split('T')[0];
          
          // Desactivar asignaciones actuales
          for (const assign of activeAssignments) {
            const assignmentDate = assign.date || assign.joinDate || today;
            const technicianEmail = assign.userEmail || assign.technicianId;
            const id = `${assign.teamId}/${technicianEmail}/${assignmentDate}`;

            // Si fecha inicio coincide con hoy se elimina
            if (assign.joinDate === now) {
              await teamAssignmentService.deleteAssignment(id);
            } else {
              // Desactivar la asignación existente
              const updatedAssignment = await teamAssignmentService.updateAssignment(id, {
                ...assign,
                leaveDate: now,
                isActive: false
              });
              const deactivateRes = { ok: true, status: 200, text: async () => JSON.stringify(updatedAssignment || {}) };
              const text = await deactivateRes.text();
              let data;
              try {
                data = text ? JSON.parse(text) : {};
              } catch (err) {
                data = { message: text };
              }
              if (!deactivateRes.ok) {
                showNotification(data.message || `HTTP error! status: ${deactivateRes.status}`, 'error');
                return;
              }
            }
          }
          
          // Crear nuevas asignaciones
          if (filteredTechnicians.length > 0) {
            const payload = {
              teamId: newTeamId,
              technicianIds: filteredTechnicians,
              vehicleId: teamForm.vehicleId,
              startDate: today,
              endDate: farFuture,
            };
            const createdAssignment = await teamAssignmentService.createAssignment(payload);
            const assignRes = { ok: true, status: 200, text: async () => JSON.stringify(createdAssignment || {}) };
            const textAssign = await assignRes.text();
            let dataAssign;
            try {
              dataAssign = textAssign ? JSON.parse(textAssign) : {};
            } catch (err) {
              dataAssign = { message: textAssign };
            }
            if (!assignRes.ok) {
              showNotification(dataAssign.message || `HTTP error! status: ${assignRes.status}`, 'error');
              return;
            }
          }
        }
      }

      // Refrescar datos y cerrar modal
      
      await fetchData();
      setShowTeamModal(false);
      setEditingTeam(null);
      clearNotification();
    } catch (err) {
      console.error('Error guardando equipo:', err);
      showNotification(getRequestErrorMessage(err, 'Error al guardar el equipo'), 'error');
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'Equipo',
      align: 'center',
      render: (row) => row.name || 'Sin nombre'
    },
    {
      key: 'assignedTechnicians',
      label: 'Tecnicos asignados',
      align: 'center',
      render: (row) => {

        const assignedTechnicians = assignments.filter(a => a.teamId === row.id && a.isActive);
        if (!assignedTechnicians.length) return '—';
        return (
          <div>
            {assignedTechnicians.map((tech, i) => (
              <div key={tech.user.id ?? i}>{i + 1}. {tech.user.name}</div>
            ))}
          </div>
        );
      }
    },
    {
      key: 'vehicle',
      label: 'Vehículo',
      align: 'center',
      render: (row) => {
        const assignedTechnicians = assignments.filter(a => a.teamId === row.id);
        if (assignedTechnicians.length > 0) {
          const vehicle = vehicles.find(v => v.id === assignedTechnicians[0].vehicleId);
          return vehicle ? vehicle.name : 'Sin vehículo';
        }
        return 'Sin vehículo';
      }
    },
    {
      key: 'actions',
      label: 'Acciones',
      align: 'center',
      isAction: true,
      render: (row) => (
        <span data-tour="team-actions" className="inline-flex">
          <ActionMenu
            options={[
              {
                label: 'Ver',
                icon: <Eye size={16} />,
                onClick: () => handleOpenEditTeam(row, 'view'),
              },
              {
                label: 'Editar',
                icon: <PencilSimple size={16} />,
                onClick: () => handleOpenEditTeam(row),
              },
              {
                label: 'Eliminar',
                icon: <Trash size={16} />,
                onClick: () => handleDeleteTeam(row),
              },
            ]}
          />
        </span>
      )
    }
  ];

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-error mb-4">{error}</p>
        <button
          onClick={fetchData}
          className="bg-amber text-on-amber px-4 py-2 rounded hover:bg-amber-hover"
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div data-tour="teams-header">
        <PageHeader
          title="Gestión de Equipos"
          subtitle="Lista de equipos y asignaciones"
          icon={<UsersThree size={28} />}
          actionButton={{
            label: 'Crear Equipo',
            icon: <Plus size={18} />,
            onClick: handleOpenCreateTeam,
          }}
        />
      </div>

      {/* Tabla de equipos */}
      <div data-tour="teams-table" className="relative task-section md:overflow-hidden md:rounded-xl md:border md:border-line md:bg-surface md:shadow-md">
        <GenericTables
          columns={columns}
          data={teams}
          emptyMessage="No hay equipos creados"
          zebraStripe={true}
          showFooter={true}
          pagination={{
            currentPage,
            totalPages,
            pageSize,
            totalItems: totalTeams,
            pageSizeOptions: [5, 10, 20, 50],
            onPageChange: setCurrentPage,
            onPageSizeChange: (newSize) => {
              setPageSize(newSize);
              setCurrentPage(1);
            }
          }}
        />
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-bg/60">
            <div className="flex items-center gap-3 rounded-lg border border-line bg-surface/90 px-4 py-3 text-sm text-content-muted shadow-lg">
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-amber border-t-transparent" />
              Consultando Equipos...
            </div>
          </div>
        )}
      </div>

      {/* Modal de Crear/Editar Equipo */}
      <ModalCreationEditing
        isOpen={showTeamModal}
        mode={teamModalMode}
        title={teamModalMode === 'view' ? 'Ver Equipo' : editingTeam ? 'Editar Equipo' : 'Crear Equipo'}
        maxWidthClassName="max-w-2xl"
        contentClassName="space-y-6"
        notification={notification}
        onClose={() => {
          setShowTeamModal(false);
          setEditingTeam(null);
        }}
        onModeChange={setTeamModalMode}
        onSubmit={handleSaveTeam}
      >
              <div data-tour="team-form-data" className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Nombre */}
                <div>
                  <label className="block text-sm font-medium text-content-muted mb-2">Nombre *</label>
                  <input
                    type="text"
                    name="name"
                    placeholder="Ej: Equipo A"
                    value={teamForm.name}
                    onChange={handleTeamFormChange}
                    onBlur={() => handleTeamFieldBlur('name')}
                    disabled={teamModalMode === 'view'}
                    className={`w-full h-12 px-3 py-2 rounded-md bg-surface-2 text-content focus:outline-none focus:ring-2 focus:ring-amber border ${teamFormTouched.name && !teamForm.name ? 'border-error' : 'border-line'}`}
                  />
                  {teamFormTouched.name && !teamForm.name && (
                    <p className="text-xs text-error mt-1">El nombre es obligatorio</p>
                  )}
                </div>

                {/* Vehículo */}
                <div>
                  <label className="block text-sm font-medium text-content-muted mb-2">Vehículo</label>
                  <select
                    name="vehicleId"
                    value={teamForm.vehicleId}
                    onChange={handleTeamFormChange}
                    onBlur={() => handleTeamFieldBlur('vehicleId')}
                    disabled={teamModalMode === 'view'}
                    className={`w-full h-12 px-3 py-2 rounded-md bg-surface-2 text-content focus:outline-none focus:ring-2 focus:ring-amber border border-line`}
                  >
                    <option value="">Seleccionar vehículo</option>
                    {Array.isArray(vehicles) && vehicles.map(vehicle => {
                      const occupied = isVehicleAssigned(vehicle.id, editingTeam?.id);
                      return (
                        <option key={vehicle.id} value={vehicle.id} disabled={occupied}>
                          {vehicle.name} {occupied ? '(Vehículo ya asignado)' : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>

                {/* Descripción - textarea que ocupa 2 columnas */}
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-content-muted">Descripción *</label>
                    <span className="text-xs text-content-muted">{teamForm.description.length}/255</span>
                  </div>
                  <textarea
                    name="description"
                    placeholder="Ej: Equipo de soporte técnico"
                    value={teamForm.description}
                    onChange={handleTeamFormChange}
                    onBlur={() => handleTeamFieldBlur('description')}
                    disabled={teamModalMode === 'view'}
                    rows="2"
                    maxLength="255"
                    className={`w-full px-3 py-2 rounded-md bg-surface-2 text-content focus:outline-none focus:ring-2 focus:ring-amber border ${teamFormTouched.description && !teamForm.description ? 'border-error' : 'border-line'} resize-none`}
                  />
                  {teamFormTouched.description && !teamForm.description && (
                    <p className="text-xs text-error mt-1">La descripción es obligatoria</p>
                  )}
                </div>


                {/* Máximo de Técnicos */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-content-muted mb-2">Máximo de Técnicos (1-10) *</label>
                  <input
                    type="number"
                    name="maxMembers"
                    placeholder="2"
                    value={teamForm.maxMembers}
                    onChange={handleTeamFormChange}
                    onBlur={() => handleTeamFieldBlur('maxMembers')}
                    onFocus={(e) => e.target.select()}
                    disabled={teamModalMode === 'view'}
                    className="w-full h-12 px-3 py-2 rounded-md bg-surface-2 text-content focus:outline-none focus:ring-2 focus:ring-amber border border-line"
                  />
                </div>
              </div>

              {/* Límites de tareas por categoría - dinámico según categorías de la company */}
              {/* En el paso del recorrido se monta abierto (key distinta) para mostrar su contenido */}
              <div data-tour="team-form-limits">
                <ExpansionPanel
                  key={tourStep?.openLimits ? 'tour' : 'form'}
                  title="Límites de tareas por categoría"
                  defaultOpen={Boolean(tourStep?.openLimits)}
                >
                  <p className="text-xs text-content-muted mb-4">
                    Cantidad máxima de tareas por día que este equipo puede tomar de cada categoría.
                  </p>

                  {!editingTeam && (
                    <p className="text-sm text-content-muted">
                      Los límites se configuran después de crear el equipo.
                    </p>
                  )}

                  {editingTeam && teamLimitsLoading && (
                    <div className="space-y-2">
                      {[0, 1, 2].map(i => (
                        <div key={i} className="h-14 rounded-md bg-surface-2 animate-pulse" />
                      ))}
                    </div>
                  )}

                  {editingTeam && !teamLimitsLoading && teamLimits.length === 0 && (
                    <p className="text-sm text-content-muted">
                      Esta empresa todavía no tiene categorías de tareas activas.
                    </p>
                  )}

                  {editingTeam && !teamLimitsLoading && teamLimits.length > 0 && (
                    <div className="space-y-2">
                      {teamLimits.map((limit) => {
                        const status = teamLimitsRowStatus[limit.categoryName];
                        return (
                          <div
                            key={limit.categoryName}
                            className={`flex items-center justify-between gap-3 bg-surface border rounded-md p-3 ${status === 'error' ? 'border-error/60' : 'border-line'}`}
                          >
                            <div className="min-w-0">
                              <div className="text-content text-sm font-small">{limit.displayName}</div>
                            </div>
                            <div className="flex items-center gap-2 flex-none">
                              <input
                                type="number"
                                min="0"
                                placeholder="Sin límite"
                                value={limit.taskLimit ?? ''}
                                onChange={(e) => handleTeamLimitChange(limit.categoryName, e.target.value)}
                                onBlur={() => handleTeamLimitBlur(limit.categoryName)}
                                disabled={teamModalMode === 'view'}
                                className="w-24 h-10 px-3 py-2 rounded-md bg-surface-2 text-content text-right focus:outline-none focus:ring-2 focus:ring-amber border border-line"
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </ExpansionPanel>
              </div>

              {teamForm.maxMembers > 0 && (
                <div data-tour="team-form-technicians" className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Array.from({ length: teamForm.maxMembers }).map((_, idx) => (
                    <div key={idx}>
                      <label className="block text-sm font-medium text-content-muted mb-2">Técnico #{idx + 1}</label>
                      <select
                        value={selectedTechnicians[idx] || ''}
                        onChange={(e) => handleSelectTechnicianChange(idx, e.target.value)}
                        disabled={teamModalMode === 'view'}
                        className="w-full h-12 px-3 py-2 rounded-md bg-surface-2 text-content focus:outline-none focus:ring-2 focus:ring-amber border border-line"
                      >
                        <option value="">Seleccionar técnico</option>
                        {Array.isArray(technicians) && technicians.map(tech => {
                          const alreadySelected = selectedTechnicians.includes(tech.email) && selectedTechnicians[idx] !== tech.email;
                          const assignedElsewhere = editingTeam ? isTechnicianAssignedElsewhere(tech.email, editingTeam.id) : isTechnicianAssigned(tech.email);
                          const disabled = alreadySelected || assignedElsewhere;
                          return (
                            <option key={tech.email} value={tech.email} disabled={disabled}>
                              {tech.name} {disabled ? '(Técnico ya asignado)' : ''}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  ))}
                </div>
              )}

      </ModalCreationEditing>

      {/* Confirmación de Eliminación */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="Confirmar Eliminación"
        message={`¿Estás seguro de que quieres eliminar el equipo "${deleteConfirm.team?.name}"? Esta acción no se puede deshacer.`}
        onConfirm={confirmDeleteTeam}
        onCancel={cancelDeleteTeam}
        confirmText="Eliminar"
        cancelText="Cancelar"
      >
        {deleteConfirm.activeTasks > 0 && (
          <div className="mt-4 flex items-start gap-2 bg-warning/12 border border-warning/60 rounded-lg p-3">
            <p className="text-warning text-sm">
              Este equipo tiene <strong>{deleteConfirm.activeTasks}</strong> tarea{deleteConfirm.activeTasks !== 1 ? 's' : ''} sin finalizar ni cancelar{deleteConfirm.activeTasks !== 1 ? 's' : ''}.
              Eliminándolo podría dejar esas tareas sin equipo asignado.
            </p>
          </div>
        )}
      </ConfirmDialog>
    </div>
  );
};

export default TeamManagement;
